import React, { useState, useEffect, useMemo } from 'react';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import {
  assignManualKnife,
  getAllManualKnifeAssignments,
  cancelManualKnifeAssignment,
  removeManualKnife,
  assignBulkKnife
} from '../../services/manualKnifeService';
import { getAllUsers } from '../../firebase/helpers';
import { collection, onSnapshot, query, orderBy, getDocs, where } from 'firebase/firestore';
import { db } from '../../firebase/config';

function ManualKnifeManagement() {
  const { isAdmin } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedCard, setExpandedCard] = useState(null);

  // Manual knife assignments
  const [knifeAssignments, setKnifeAssignments] = useState([]);
  const [assignmentFormData, setAssignmentFormData] = useState({
    userId: '',
    knifeAmount: '',
    reason: ''
  });
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  
  // VIP Filter System
  const [vipFilter, setVipFilter] = useState('all'); // 'all', 'VIP1', 'VIP2', 'VIP3', 'VIP4'
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState(1); // 1: Select VIP, 2: Set Amount, 3: Confirm
  const [bulkSelectedVip, setBulkSelectedVip] = useState(null);
  const [bulkKnifeAmount, setBulkKnifeAmount] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [assignmentVipFilter, setAssignmentVipFilter] = useState('all');

  useEffect(() => {
    if (isAdmin) {
      loadKnifeAssignments();
      loadUsers();
      
      // Set up real-time listener for manual knife assignments
      const assignmentsRef = collection(db, 'manualKnifeAssignments');
      const assignmentsQuery = query(assignmentsRef, orderBy('assignedAt', 'desc'));
      const unsubscribeAssignments = onSnapshot(assignmentsQuery, async () => {
        try {
          const assignments = await getAllManualKnifeAssignments();
          setKnifeAssignments(assignments);
        } catch (err) {
          console.error('Error reloading assignments:', err);
        }
      });

      return () => {
        unsubscribeAssignments();
      };
    }
  }, [isAdmin]);

  const loadKnifeAssignments = async () => {
    try {
      setAssignmentLoading(true);
      const assignments = await getAllManualKnifeAssignments();
      setKnifeAssignments(assignments);
    } catch (err) {
      console.error('Error loading knife assignments:', err);
      setError('Failed to load knife assignments');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const users = await getAllUsers();
      
      const adminUsersRef = collection(db, 'adminUsers');
      const adminUsersSnapshot = await getDocs(adminUsersRef);
      const adminUids = new Set();
      const adminEmails = new Set();
      
      adminUsersSnapshot.forEach((doc) => {
        const adminData = doc.data();
        adminUids.add(doc.id);
        if (adminData.email) {
          adminEmails.add(adminData.email.toLowerCase());
        }
      });
      
      const formattedUsers = users
        .filter(user => {
          if (user.role || user.admin || user.isDeleted) {
            return false;
          }
          if (adminUids.has(user.id)) {
            return false;
          }
          if (user.email && adminEmails.has(user.email.toLowerCase())) {
            return false;
          }
          return true;
        })
        .map(user => ({
          id: user.id,
          displayName: `${user.username || 'User'} (${user.email || 'No email'})`,
          email: user.email,
          username: user.username,
          vipLevel: user.vipLevel || 'VIP1',
          balances: user.balances,
          createdAt: user.createdAt,
          isActive: !user.isDeleted && !user.isSuspended,
          hasKnife: user.isKnifeApplied || user.hasNegativeBalance,
          isKnifeApplied: user.isKnifeApplied || false,
          hasNegativeBalance: user.hasNegativeBalance || false
        }));
      setAvailableUsers(formattedUsers);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleAssignKnife = async () => {
    try {
      setAssignmentLoading(true);
      setError('');
      setSuccess('');
      
      if (!assignmentFormData.userId || !assignmentFormData.knifeAmount) {
        setError('Please fill in all required fields (User and Amount)');
        return;
      }

      const knifeAmount = parseFloat(assignmentFormData.knifeAmount);
      if (isNaN(knifeAmount) || knifeAmount <= 0) {
        setError('Knife amount must be greater than 0');
        return;
      }

      // Get selected user's balance
      const selectedUser = availableUsers.find(u => u.id === assignmentFormData.userId);
      if (!selectedUser) {
        setError('Selected user not found');
        setAssignmentLoading(false);
        return;
      }

      // CRITICAL RULE: Check if user already has an ACTIVE knife
      // A user can only have ONE active knife at a time
      // Check 1: Active knife transactions in database (most reliable)
      try {
        const knifeTransactionsRef = collection(db, 'knifeTransactions');
        const activeKnifeQuery = query(
          knifeTransactionsRef,
          where('userId', '==', assignmentFormData.userId),
          where('status', '==', 'active')
        );
        const activeKnifeSnapshot = await getDocs(activeKnifeQuery);
        if (!activeKnifeSnapshot.empty) {
          const activeKnife = activeKnifeSnapshot.docs[0].data();
          setError(
            `❌ User already has an active knife transaction!\n\n` +
            `Current knife amount: ${activeKnife.knifeAmount || 'N/A'} USDT\n\n` +
            `Please remove the current knife before assigning a new one. ` +
            `A user can only have ONE active knife at a time.`
          );
          setAssignmentLoading(false);
          return;
        }
      } catch (checkError) {
        console.error('Error checking active knife transactions:', checkError);
        // Continue with other checks
      }
      
      // Check 2: Applied assignments
      try {
        const assignmentsRef = collection(db, 'manualKnifeAssignments');
        const appliedAssignmentQuery = query(
          assignmentsRef,
          where('userId', '==', assignmentFormData.userId),
          where('status', '==', 'applied')
        );
        const appliedAssignmentSnapshot = await getDocs(appliedAssignmentQuery);
        if (!appliedAssignmentSnapshot.empty) {
          const appliedAssignment = appliedAssignmentSnapshot.docs[0].data();
          setError(
            `❌ User already has an applied knife assignment!\n\n` +
            `Current knife amount: ${appliedAssignment.knifeAmount || 'N/A'} USDT\n\n` +
            `Please remove the current knife before assigning a new one. ` +
            `A user can only have ONE active knife at a time.`
          );
          setAssignmentLoading(false);
          return;
        }
      } catch (checkError) {
        console.error('Error checking applied assignments:', checkError);
        // Continue with other checks
      }
      
      // Check 3: User object fields (as backup check)
      if (selectedUser.isKnifeApplied || selectedUser.hasNegativeBalance) {
        const currentKnifeAmount = selectedUser.balances?.knifeAmount || 'N/A';
        setError(
          `❌ User already has an active knife applied!\n\n` +
          `Current knife amount: ${currentKnifeAmount} USDT\n\n` +
          `Please remove the current knife before assigning a new one. ` +
          `A user can only have ONE active knife at a time.`
        );
        setAssignmentLoading(false);
        return;
      }

      const userBalance = selectedUser.balances?.total || selectedUser.balances?.wallet || 0;

      // CRITICAL RULE: Knife amount MUST be greater than user's current balance
      if (knifeAmount <= userBalance) {
        setError(`Knife amount (${knifeAmount.toFixed(2)} USDT) MUST be greater than user's current balance (${userBalance.toFixed(2)} USDT). This creates the negative balance scenario required for optimization.`);
        setAssignmentLoading(false);
        return;
      }
      
      const result = await assignManualKnife(
        assignmentFormData.userId,
        knifeAmount,
        assignmentFormData.reason
      );
      
      if (result.success) {
        setSuccess(`Knife assigned successfully! ${result.action === 'updated' ? 'Assignment updated.' : 'New assignment created.'}`);
        setAssignmentFormData({
          userId: '',
          knifeAmount: '',
          reason: ''
        });
        // Refresh both assignments and users to get latest status
        await loadKnifeAssignments();
        await loadUsers();
      }
    } catch (err) {
      console.error('Error assigning knife:', err);
      setError(err.message || 'Failed to assign knife');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleCancelAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to cancel this assignment?')) {
      return;
    }
    
    try {
      setAssignmentLoading(true);
      await cancelManualKnifeAssignment(assignmentId);
      setSuccess('Assignment cancelled successfully');
      await loadKnifeAssignments();
    } catch (err) {
      console.error('Error cancelling assignment:', err);
      setError(err.message || 'Failed to cancel assignment');
    } finally {
      setAssignmentLoading(false);
    }
  };

  // Handle bulk knife assignment
  const handleBulkKnifeAssignment = async () => {
    if (!bulkSelectedVip || !bulkKnifeAmount) {
      setError('Please select VIP level and enter knife amount');
      return;
    }

    const knifeAmount = parseFloat(bulkKnifeAmount);
    if (isNaN(knifeAmount) || knifeAmount <= 0) {
      setError('Knife amount must be greater than 0');
      return;
    }

    try {
      setBulkProcessing(true);
      setError('');
      setSuccess('');

      // Get users with selected VIP level
      const targetUsers = availableUsers.filter(user => user.vipLevel === bulkSelectedVip);
      
      if (targetUsers.length === 0) {
        setError(`No ${bulkSelectedVip} users found`);
        setBulkProcessing(false);
        return;
      }

      // CRITICAL RULE: Validate that knife amount is greater than each user's balance
      const invalidUsers = [];
      targetUsers.forEach(user => {
        const userBalance = user.balances?.total || user.balances?.wallet || 0;
        if (knifeAmount <= userBalance) {
          invalidUsers.push({
            email: user.email || 'Unknown',
            balance: userBalance,
            required: userBalance + 0.01
          });
        }
      });

      if (invalidUsers.length > 0) {
        const invalidList = invalidUsers.slice(0, 5).map(u => 
          `  • ${u.email}: Balance ${u.balance.toFixed(2)} USDT (Need > ${u.balance.toFixed(2)} USDT)`
        ).join('\n');
        const moreCount = invalidUsers.length > 5 ? `\n  ... and ${invalidUsers.length - 5} more` : '';
        
        setError(
          `Knife amount (${knifeAmount.toFixed(2)} USDT) MUST be greater than each user's balance.\n\n` +
          `Invalid users (${invalidUsers.length}):\n${invalidList}${moreCount}\n\n` +
          `Please increase the knife amount to at least ${Math.max(...invalidUsers.map(u => u.required)).toFixed(2)} USDT to cover all users.`
        );
        setBulkProcessing(false);
        return;
      }

      setBulkProgress({ current: 0, total: targetUsers.length });

      // Call bulk assignment service
      const result = await assignBulkKnife(
        targetUsers.map(u => u.id),
        knifeAmount,
        bulkReason
      );

      if (result.success) {
        setSuccess(`✅ Knife applied to ${result.successCount} ${bulkSelectedVip} users successfully!${result.failedCount > 0 ? ` ${result.failedCount} failed.` : ''}`);
        setShowBulkModal(false);
        setBulkStep(1);
        setBulkSelectedVip(null);
        setBulkKnifeAmount('');
        setBulkReason('');
        setBulkProgress({ current: 0, total: 0 });
        await loadKnifeAssignments();
        await loadUsers();
      } else {
        setError(result.error || 'Failed to assign bulk knife');
      }
    } catch (err) {
      console.error('Error in bulk knife assignment:', err);
      setError(err.message || 'Failed to assign bulk knife');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleRemoveKnife = async (assignmentId) => {
    // Get assignment details to show accurate profit calculation
    try {
      const assignment = knifeAssignments.find(a => a.id === assignmentId);
      if (assignment && assignment.user) {
        const user = assignment.user;
        const knifeAmount = assignment.knifeAmount || 0;
        const vipLevel = user.vipLevel || 'VIP1';
        const vipMultipliers = {
          'VIP1': 0.4,
          'VIP2': 0.6,
          'VIP3': 0.8,
          'VIP4': 1.0
        };
        const vipMultiplier = vipMultipliers[vipLevel] || 0.4;
        
        // Estimate negative amount (we don't have exact user balance here, but we can show the formula)
        const confirmMessage = `Are you sure you want to remove this knife?\n\n` +
          `Knife Amount: ${knifeAmount.toFixed(2)} USDT\n` +
          `User VIP Level: ${vipLevel}\n\n` +
          `Profit Calculation:\n` +
          `15% of negative amount × ${vipLevel} multiplier (${(vipMultiplier * 100).toFixed(1)}%)\n\n` +
          `The user's account access will be restored after profit is added.`;
        
        if (!window.confirm(confirmMessage)) {
          return;
        }
      } else {
        if (!window.confirm('Are you sure you want to remove this knife? The user will receive profit (15% of negative amount × VIP level multiplier) and their account access will be restored.')) {
          return;
        }
      }
    } catch (err) {
      if (!window.confirm('Are you sure you want to remove this knife? The user will receive profit and their account access will be restored.')) {
        return;
      }
    }
    
    try {
      setAssignmentLoading(true);
      setError('');
      setSuccess('');
      
      const result = await removeManualKnife(assignmentId);
      
      if (result.success) {
        const profitInfo = result.negativeAmount 
          ? `Profit: ${result.profitAmount.toFixed(2)} USDT (15% of ${result.negativeAmount.toFixed(2)} negative × ${result.vipLevel || 'VIP1'} multiplier)`
          : `Profit: ${result.profitAmount.toFixed(2)} USDT`;
        setSuccess(`Knife removed successfully! User received ${profitInfo}. Account access restored.`);
        await loadKnifeAssignments();
      }
    } catch (err) {
      console.error('Error removing knife:', err);
      setError(err.message || 'Failed to remove knife');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedAssignments = useMemo(() => {
    if (!sortConfig.key) return knifeAssignments;
    
    return [...knifeAssignments].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'user':
          aVal = (a.user?.email || a.userEmail || '').toLowerCase();
          bVal = (b.user?.email || b.userEmail || '').toLowerCase();
          break;
        case 'amount':
          aVal = a.knifeAmount || 0;
          bVal = b.knifeAmount || 0;
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'assignedAt':
          aVal = a.assignedAt?.toDate?.()?.getTime() || 0;
          bVal = b.assignedAt?.toDate?.()?.getTime() || 0;
          break;
        case 'appliedAt':
          aVal = a.appliedAt?.toDate?.()?.getTime() || 0;
          bVal = b.appliedAt?.toDate?.()?.getTime() || 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [knifeAssignments, sortConfig]);

  const formatDate = (date, isMobile = false) => {
    if (!date) return '-';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    if (isMobile) {
      return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return dateObj.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'knife-status-pending';
      case 'applied':
        return 'knife-status-applied';
      case 'cancelled':
        return 'knife-status-cancelled';
      case 'removed':
        return 'knife-status-removed';
      default:
        return 'knife-status-default';
    }
  };

  if (!isAdmin) {
    return (
      <div className="knife-management-container">
        <div className="knife-access-denied">
          <i className="bi bi-shield-x"></i>
          <h3>Access Denied</h3>
          <p>You do not have permission to access this section.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="knife-management-container">
      {/* Header Section */}
      <div className="knife-header">
        <div className="knife-header-content">
          <div>
            <h1 className="knife-title">
              <i className="bi bi-dagger"></i>
              <span>Manual Knife Management</span>
            </h1>
            <p className="knife-subtitle">
              Assign knives directly to users. The knife will automatically apply when the user submits a task.
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      {success && (
        <div className="knife-alert knife-alert-success" role="alert">
          <i className="bi bi-check-circle-fill"></i>
          <span>{success}</span>
          <button 
            type="button" 
            className="knife-alert-close" 
            onClick={() => setSuccess('')}
            aria-label="Close"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {error && (
        <div className="knife-alert knife-alert-error" role="alert">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <span>{error}</span>
          <button 
            type="button" 
            className="knife-alert-close" 
            onClick={() => setError('')}
            aria-label="Close"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {/* VIP Filter and Statistics Section */}
      <VIPFilterSection 
        vipFilter={vipFilter}
        setVipFilter={setVipFilter}
        availableUsers={availableUsers}
        knifeAssignments={knifeAssignments}
        onBulkAssignmentClick={() => setShowBulkModal(true)}
      />

      {/* User Cards View (when VIP filter is active) */}
      {vipFilter !== 'all' && (
        <UserCardsView
          users={availableUsers}
          vipFilter={vipFilter}
          onAssignKnife={(userId) => {
            setAssignmentFormData({ ...assignmentFormData, userId });
            // Scroll to form
            setTimeout(() => {
              document.querySelector('.knife-form-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          }}
        />
      )}

      {/* Assignment Form */}
      <div className="knife-card knife-form-card">
        <div className="knife-card-header">
          <div className="knife-card-header-icon">
            <i className="bi bi-plus-circle"></i>
          </div>
          <div>
            <h2 className="knife-card-title">Create New Assignment</h2>
            <p className="knife-card-subtitle">Fill in the details to assign a knife to a user</p>
          </div>
        </div>
        <div className="knife-card-body">
          <form onSubmit={(e) => { e.preventDefault(); handleAssignKnife(); }}>
            <div className="knife-form-grid">
              <div className="knife-form-group knife-form-group-full">
                <label htmlFor="userId" className="knife-form-label">
                  User <span className="knife-required">*</span>
                </label>
                <select
                  id="userId"
                  className="knife-form-input knife-form-select"
                  value={assignmentFormData.userId}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, userId: e.target.value })}
                  required
                  disabled={assignmentLoading}
                >
                  <option value="">Select a user...</option>
                  {availableUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.displayName} - {user.vipLevel}
                    </option>
                  ))}
                </select>
              </div>

              <div className="knife-form-group">
                <label htmlFor="knifeAmount" className="knife-form-label">
                  Knife Amount (USDT) <span className="knife-required">*</span>
                  <span style={{ fontSize: '0.75rem', color: '#dc2626', marginLeft: '8px', fontWeight: '600' }}>
                    MUST be greater than user's balance
                  </span>
                </label>
                <div className="knife-input-wrapper">
                  <input
                    type="number"
                    id="knifeAmount"
                    className="knife-form-input"
                    placeholder="0.00"
                    value={assignmentFormData.knifeAmount}
                    onChange={(e) => setAssignmentFormData({ ...assignmentFormData, knifeAmount: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                    disabled={assignmentLoading}
                  />
                  <span className="knife-input-suffix">USDT</span>
                </div>
                {assignmentFormData.userId && assignmentFormData.knifeAmount && (() => {
                  const selectedUser = availableUsers.find(u => u.id === assignmentFormData.userId);
                  if (!selectedUser) return null;
                  const userBalance = selectedUser.balances?.total || selectedUser.balances?.wallet || 0;
                  const knifeAmount = parseFloat(assignmentFormData.knifeAmount) || 0;
                  const isValid = knifeAmount > userBalance;
                  const negativeAmount = knifeAmount - userBalance;
                  
                  return (
                    <div style={{ marginTop: '8px' }}>
                      {selectedUser && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px' }}>
                          User's current balance: <strong>{userBalance.toFixed(2)} USDT</strong>
                        </div>
                      )}
                      {knifeAmount > 0 && (
                        <div style={{ 
                          fontSize: '0.75rem', 
                          padding: '8px',
                          borderRadius: '6px',
                          marginTop: '4px',
                          background: isValid ? '#d1fae5' : '#fee2e2',
                          color: isValid ? '#065f46' : '#991b1b',
                          border: `1px solid ${isValid ? '#10b981' : '#dc2626'}`
                        }}>
                          {isValid ? (
                            <>
                              <i className="bi bi-check-circle-fill me-1"></i>
                              <strong>Valid:</strong> Knife amount ({knifeAmount.toFixed(2)} USDT) is greater than balance ({userBalance.toFixed(2)} USDT).
                              <div style={{ marginTop: '4px' }}>
                                Negative balance created: <strong>{negativeAmount.toFixed(2)} USDT</strong>
                              </div>
                            </>
                          ) : (
                            <>
                              <i className="bi bi-exclamation-triangle-fill me-1"></i>
                              <strong>Invalid:</strong> Knife amount ({knifeAmount.toFixed(2)} USDT) must be greater than user's balance ({userBalance.toFixed(2)} USDT).
                              <div style={{ marginTop: '4px' }}>
                                Minimum required: <strong>{(userBalance + 0.01).toFixed(2)} USDT</strong>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div className="knife-form-group">
                <label htmlFor="reason" className="knife-form-label">
                  Reason <span className="knife-optional">(Optional)</span>
                </label>
                <input
                  type="text"
                  id="reason"
                  className="knife-form-input"
                  placeholder="Enter reason for assignment..."
                  value={assignmentFormData.reason}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, reason: e.target.value })}
                  disabled={assignmentLoading}
                />
              </div>
            </div>

            <div className="knife-form-actions">
              <button
                type="submit"
                className="knife-btn knife-btn-primary"
                disabled={assignmentLoading || !assignmentFormData.userId || !assignmentFormData.knifeAmount || (() => {
                  if (!assignmentFormData.userId || !assignmentFormData.knifeAmount) return true;
                  const selectedUser = availableUsers.find(u => u.id === assignmentFormData.userId);
                  if (!selectedUser) return true;
                  const userBalance = selectedUser.balances?.total || selectedUser.balances?.wallet || 0;
                  const knifeAmount = parseFloat(assignmentFormData.knifeAmount) || 0;
                  return knifeAmount <= userBalance;
                })()}
              >
                {assignmentLoading ? (
                  <>
                    <span className="knife-spinner"></span>
                    Assigning...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle"></i>
                    Assign Knife
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Assignments List */}
      <div className="knife-card">
        <div className="knife-card-header">
          <div className="knife-card-header-icon">
            <i className="bi bi-list-ul"></i>
          </div>
          <div className="knife-card-header-content">
            <div>
              <h2 className="knife-card-title">All Assignments</h2>
              <p className="knife-card-subtitle">
                {knifeAssignments.length} {knifeAssignments.length === 1 ? 'assignment' : 'assignments'} total
              </p>
            </div>
            <button
              className="knife-btn knife-btn-secondary knife-btn-icon"
              onClick={loadKnifeAssignments}
              disabled={assignmentLoading}
              aria-label="Refresh assignments"
            >
              <i className="bi bi-arrow-clockwise"></i>
              <span className="knife-btn-text">Refresh</span>
            </button>
          </div>
        </div>

        {/* VIP Filter for Assignments */}
        <div className="knife-vip-filter-bar" style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <span style={{ fontWeight: '500', marginRight: '8px' }}>Filter by VIP:</span>
            {['all', 'VIP1', 'VIP2', 'VIP3', 'VIP4'].map(level => (
              <button
                key={level}
                className={`knife-vip-filter-btn ${assignmentVipFilter === level ? 'active' : ''}`}
                onClick={() => setAssignmentVipFilter(level)}
                style={{
                  padding: '6px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: assignmentVipFilter === level ? '#6366f1' : 'white',
                  color: assignmentVipFilter === level ? 'white' : '#374151',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {level === 'all' ? 'All' : level}
                {level !== 'all' && (
                  <span style={{ marginLeft: '6px', opacity: 0.8 }}>
                    ({knifeAssignments.filter(a => a.user?.vipLevel === level).length})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="knife-card-body">
          {assignmentLoading && knifeAssignments.length === 0 ? (
            <div className="knife-loading-state">
              <div className="knife-skeleton-loader">
                <div className="knife-skeleton-line"></div>
                <div className="knife-skeleton-line"></div>
                <div className="knife-skeleton-line"></div>
              </div>
            </div>
          ) : knifeAssignments.length === 0 ? (
            <div className="knife-empty-state">
              <i className="bi bi-inbox"></i>
              <h3>No Assignments Found</h3>
              <p>Start by creating your first knife assignment above.</p>
            </div>
          ) : (
            <>
              {/* Desktop/Tablet Table View */}
              <div className="knife-table-container">
                <table className="knife-table">
                  <thead>
                    <tr>
                      <th 
                        className="knife-table-header knife-sortable"
                        onClick={() => handleSort('user')}
                      >
                        User
                        {sortConfig.key === 'user' && (
                          <i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th 
                        className="knife-table-header knife-sortable"
                        onClick={() => handleSort('amount')}
                      >
                        Amount
                        {sortConfig.key === 'amount' && (
                          <i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th 
                        className="knife-table-header knife-sortable"
                        onClick={() => handleSort('status')}
                      >
                        Status
                        {sortConfig.key === 'status' && (
                          <i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th 
                        className="knife-table-header knife-sortable knife-hide-mobile"
                        onClick={() => handleSort('assignedAt')}
                      >
                        Assigned At
                        {sortConfig.key === 'assignedAt' && (
                          <i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th 
                        className="knife-table-header knife-sortable knife-hide-tablet"
                        onClick={() => handleSort('appliedAt')}
                      >
                        Applied At
                        {sortConfig.key === 'appliedAt' && (
                          <i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th className="knife-table-header knife-hide-mobile">Reason</th>
                      <th className="knife-table-header knife-table-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAssignments
                      .filter(assignment => assignmentVipFilter === 'all' || assignment.user?.vipLevel === assignmentVipFilter)
                      .map((assignment) => (
                      <tr key={assignment.id} className="knife-table-row">
                        <td className="knife-table-cell">
                          <div className="knife-user-info">
                            <span className="knife-user-email">
                              {assignment.user?.email || assignment.userEmail || 'N/A'}
                            </span>
                            {assignment.user?.vipLevel && (
                              <span className={`knife-vip-badge knife-vip-${assignment.user.vipLevel.toLowerCase()}`}>
                                {assignment.user.vipLevel}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="knife-table-cell">
                          <div>
                            <span className="knife-amount">
                              {assignment.knifeAmount.toFixed(2)} <span className="knife-currency">USDT</span>
                            </span>
                            {assignment.status === 'applied' && assignment.user?.balances && (
                              <div className="mt-1" style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                {assignment.user.balances.negativeAmount > 0 && (
                                  <div>Negative: {assignment.user.balances.negativeAmount.toFixed(2)} USDT</div>
                                )}
                                {assignment.user.balances.frozen > 0 && (
                                  <div>Frozen: {assignment.user.balances.frozen.toFixed(2)} USDT</div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="knife-table-cell">
                          <span className={`knife-status-badge ${getStatusBadgeClass(assignment.status)}`}>
                            {assignment.status}
                          </span>
                        </td>
                        <td className="knife-table-cell knife-hide-mobile">
                          <span className="knife-date">{formatDate(assignment.assignedAt)}</span>
                        </td>
                        <td className="knife-table-cell knife-hide-tablet">
                          <span className="knife-date">
                            {assignment.appliedAt ? formatDate(assignment.appliedAt) : '-'}
                          </span>
                          {assignment.status === 'removed' && assignment.removedAt && (
                            <div className="knife-date-secondary">
                              Removed: {formatDate(assignment.removedAt)}
                            </div>
                          )}
                        </td>
                        <td className="knife-table-cell knife-hide-mobile">
                          <span className="knife-reason">{assignment.reason || '-'}</span>
                        </td>
                        <td className="knife-table-cell knife-table-actions">
                          <div className="knife-action-buttons">
                            {assignment.status === 'pending' && (
                              <button
                                className="knife-btn knife-btn-danger knife-btn-sm"
                                onClick={() => handleCancelAssignment(assignment.id)}
                                disabled={assignmentLoading}
                                aria-label="Cancel assignment"
                              >
                                <i className="bi bi-x-circle"></i>
                                <span className="knife-btn-text">Cancel</span>
                              </button>
                            )}
                            {assignment.status === 'applied' && (
                              <button
                                className="knife-btn knife-btn-success knife-btn-sm"
                                onClick={() => handleRemoveKnife(assignment.id)}
                                disabled={assignmentLoading}
                                aria-label="Remove knife"
                                title="Remove knife and restore account access. User will receive profit (3x knife amount)."
                              >
                                <i className="bi bi-check-circle"></i>
                                <span className="knife-btn-text">Remove</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="knife-mobile-cards">
                {sortedAssignments
                  .filter(assignment => assignmentVipFilter === 'all' || assignment.user?.vipLevel === assignmentVipFilter)
                  .map((assignment) => (
                  <div 
                    key={assignment.id} 
                    className={`knife-mobile-card ${expandedCard === assignment.id ? 'knife-card-expanded' : ''}`}
                  >
                    <div 
                      className="knife-mobile-card-header"
                      onClick={() => setExpandedCard(expandedCard === assignment.id ? null : assignment.id)}
                    >
                      <div className="knife-mobile-card-main">
                        <div className="knife-mobile-card-user">
                          <span className="knife-user-email">
                            {assignment.user?.email || assignment.userEmail || 'N/A'}
                          </span>
                          {assignment.user?.vipLevel && (
                            <span className={`knife-vip-badge knife-vip-${assignment.user.vipLevel.toLowerCase()}`}>
                              {assignment.user.vipLevel}
                            </span>
                          )}
                        </div>
                        <div className="knife-mobile-card-amount">
                          <div>{assignment.knifeAmount.toFixed(2)} <span className="knife-currency">USDT</span></div>
                          {assignment.status === 'applied' && assignment.user?.balances && (
                            <div style={{ fontSize: '0.7rem', color: '#6c757d', marginTop: '4px' }}>
                              {assignment.user.balances.negativeAmount > 0 && (
                                <div>Negative: {assignment.user.balances.negativeAmount.toFixed(2)} USDT</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="knife-mobile-card-status">
                        <span className={`knife-status-badge ${getStatusBadgeClass(assignment.status)}`}>
                          {assignment.status}
                        </span>
                        <i className={`bi bi-chevron-${expandedCard === assignment.id ? 'up' : 'down'}`}></i>
                      </div>
                    </div>
                    {expandedCard === assignment.id && (
                      <div className="knife-mobile-card-body">
                        <div className="knife-mobile-card-detail">
                          <span className="knife-detail-label">Assigned At</span>
                          <span className="knife-detail-value">{formatDate(assignment.assignedAt, true)}</span>
                        </div>
                        {assignment.appliedAt && (
                          <div className="knife-mobile-card-detail">
                            <span className="knife-detail-label">Applied At</span>
                            <span className="knife-detail-value">{formatDate(assignment.appliedAt, true)}</span>
                          </div>
                        )}
                        {assignment.status === 'removed' && assignment.removedAt && (
                          <div className="knife-mobile-card-detail">
                            <span className="knife-detail-label">Removed At</span>
                            <span className="knife-detail-value">{formatDate(assignment.removedAt, true)}</span>
                          </div>
                        )}
                        {assignment.reason && (
                          <div className="knife-mobile-card-detail">
                            <span className="knife-detail-label">Reason</span>
                            <span className="knife-detail-value">{assignment.reason}</span>
                          </div>
                        )}
                        <div className="knife-mobile-card-actions">
                          {assignment.status === 'pending' && (
                            <button
                              className="knife-btn knife-btn-danger knife-btn-block"
                              onClick={() => handleCancelAssignment(assignment.id)}
                              disabled={assignmentLoading}
                            >
                              <i className="bi bi-x-circle"></i>
                              Cancel Assignment
                            </button>
                          )}
                          {assignment.status === 'applied' && (
                            <button
                              className="knife-btn knife-btn-success knife-btn-block"
                              onClick={() => handleRemoveKnife(assignment.id)}
                              disabled={assignmentLoading}
                            >
                              <i className="bi bi-check-circle"></i>
                              Remove Knife
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bulk Knife Assignment Modal */}
      {showBulkModal && (
        <BulkKnifeAssignmentModal
          step={bulkStep}
          selectedVip={bulkSelectedVip}
          knifeAmount={bulkKnifeAmount}
          reason={bulkReason}
          availableUsers={availableUsers}
          processing={bulkProcessing}
          progress={bulkProgress}
          onClose={() => {
            setShowBulkModal(false);
            setBulkStep(1);
            setBulkSelectedVip(null);
            setBulkKnifeAmount('');
            setBulkReason('');
            setBulkProgress({ current: 0, total: 0 });
          }}
          onStepChange={setBulkStep}
          onVipSelect={setBulkSelectedVip}
          onAmountChange={setBulkKnifeAmount}
          onReasonChange={setBulkReason}
          onConfirm={handleBulkKnifeAssignment}
        />
      )}
    </div>
  );
}

// VIP Filter Section Component
function VIPFilterSection({ vipFilter, setVipFilter, availableUsers, knifeAssignments, onBulkAssignmentClick }) {
  const vipStats = useMemo(() => {
    const stats = {
      all: availableUsers.length,
      VIP1: 0,
      VIP2: 0,
      VIP3: 0,
      VIP4: 0,
      activeKnives: 0,
      totalKnifeAmount: 0
    };

    availableUsers.forEach(user => {
      const level = user.vipLevel || 'VIP1';
      if (stats[level] !== undefined) {
        stats[level]++;
      }
    });

    knifeAssignments.forEach(assignment => {
      if (assignment.status === 'applied' || assignment.status === 'pending') {
        stats.activeKnives++;
        stats.totalKnifeAmount += assignment.knifeAmount || 0;
      }
    });

    return stats;
  }, [availableUsers, knifeAssignments]);

  return (
    <div className="knife-card" style={{ marginBottom: '24px' }}>
      {/* Statistics Display */}
      <div style={{ padding: '20px', background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', borderRadius: '12px', marginBottom: '16px' }}>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Total Users</div>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937' }}>{vipStats.all}</div>
          </div>
          <div className="d-flex gap-4 flex-wrap">
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>VIP1</div>
              <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#3b82f6' }}>{vipStats.VIP1}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>VIP2</div>
              <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#10b981' }}>{vipStats.VIP2}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>VIP3</div>
              <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#a855f7' }}>{vipStats.VIP3}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>VIP4</div>
              <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#f59e0b' }}>{vipStats.VIP4}</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Active Knives</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937' }}>
              {vipStats.activeKnives} • ${vipStats.totalKnifeAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </div>

      {/* VIP Filter Buttons */}
      <div className="d-flex flex-wrap gap-2 align-items-center" style={{ padding: '0 20px 20px' }}>
        <span style={{ fontWeight: '500', marginRight: '8px', fontSize: '0.9375rem' }}>Filter Users:</span>
        {['all', 'VIP1', 'VIP2', 'VIP3', 'VIP4'].map(level => (
          <button
            key={level}
            className={`knife-vip-filter-btn ${vipFilter === level ? 'active' : ''}`}
            onClick={() => setVipFilter(level)}
            style={{
              padding: '8px 20px',
              borderRadius: '10px',
              border: `2px solid ${vipFilter === level ? '#6366f1' : '#e5e7eb'}`,
              background: vipFilter === level ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : 'white',
              color: vipFilter === level ? 'white' : '#374151',
              fontWeight: '600',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: vipFilter === level ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none'
            }}
          >
            {level === 'all' ? 'All Users' : level}
            {level !== 'all' && (
              <span style={{ marginLeft: '8px', opacity: 0.9 }}>
                ({vipStats[level]})
              </span>
            )}
          </button>
        ))}
        <button
          onClick={onBulkAssignmentClick}
          style={{
            padding: '8px 20px',
            borderRadius: '10px',
            border: '2px solid #f59e0b',
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            color: 'white',
            fontWeight: '600',
            fontSize: '0.875rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginLeft: 'auto',
            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.3)';
          }}
        >
          <i className="bi bi-dagger me-2"></i>
          Bulk Knife Assignment
        </button>
      </div>
    </div>
  );
}

// User Cards View Component
function UserCardsView({ users, vipFilter, onAssignKnife }) {
  const filteredUsers = useMemo(() => {
    if (vipFilter === 'all') return [];
    return users.filter(user => user.vipLevel === vipFilter);
  }, [users, vipFilter]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getVIPBadgeColor = (vipLevel) => {
    const colors = {
      'VIP1': { bg: 'linear-gradient(135deg, #3b82f6, #2563eb)', text: 'white' },
      'VIP2': { bg: 'linear-gradient(135deg, #10b981, #059669)', text: 'white' },
      'VIP3': { bg: 'linear-gradient(135deg, #a855f7, #7c3aed)', text: 'white' },
      'VIP4': { bg: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'white' }
    };
    return colors[vipLevel] || colors['VIP1'];
  };

  if (filteredUsers.length === 0) {
    return (
      <div className="knife-card" style={{ marginBottom: '24px' }}>
        <div className="knife-empty-state">
          <i className="bi bi-people"></i>
          <h3>No {vipFilter} Users Found</h3>
          <p>There are no users with {vipFilter} level.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="knife-card" style={{ marginBottom: '24px' }}>
      <div className="knife-card-header">
        <div>
          <h2 className="knife-card-title">{vipFilter} Users ({filteredUsers.length})</h2>
          <p className="knife-card-subtitle">Click "Set Knife" to assign a knife to a user</p>
        </div>
      </div>
      <div className="knife-card-body" style={{ padding: '16px', overflow: 'hidden', width: '100%', boxSizing: 'border-box' }}>
        <div className="vip-user-cards-grid" style={{ width: '100%', boxSizing: 'border-box', overflow: 'visible' }}>
          {filteredUsers.map(user => {
            const vipBadgeStyle = getVIPBadgeColor(user.vipLevel);
            const balance = user.balances?.total || user.balances?.wallet || 0;
            const hasActiveKnife = user.isKnifeApplied || user.hasNegativeBalance;
            const currentKnifeAmount = user.balances?.knifeAmount || null;
            
            return (
              <div key={user.id} className="vip-user-card">
                <div className="vip-user-card-header">
                  <span className={`vip-user-status ${user.isActive ? 'active' : 'inactive'}`}>
                    <i className={`bi bi-circle-fill ${user.isActive ? 'text-success' : 'text-secondary'}`}></i>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                  {hasActiveKnife && (
                    <span className="vip-user-knife-badge">
                      <i className="bi bi-dagger"></i>
                      Knife Active
                    </span>
                  )}
                </div>
                <div className="vip-user-card-body">
                  <div className="vip-user-info-item">
                    <i className="bi bi-envelope"></i>
                    <span className="vip-user-email" style={{ display: 'block', width: '100%' }}>{user.email || 'No email'}</span>
                  </div>
                  <div className="vip-user-info-item">
                    <i className="bi bi-person"></i>
                    <span style={{ display: 'block', width: '100%' }}>Username: <strong>{user.username || 'N/A'}</strong></span>
                  </div>
                  <div className="vip-user-info-item">
                    <i className="bi bi-wallet2"></i>
                    <span style={{ display: 'block', width: '100%' }}>Balance: <strong style={{ color: '#059669' }}>{formatCurrency(balance)}</strong></span>
                  </div>
                  <div className="vip-user-info-item" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                    <i className="bi bi-star"></i>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', width: '100%', minWidth: 0 }}>
                      <span style={{ whiteSpace: 'nowrap' }}>VIP Level:</span>
                      <span 
                        className="vip-user-badge"
                        style={{
                          background: vipBadgeStyle.bg,
                          color: vipBadgeStyle.text,
                          padding: '4px 12px',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          display: 'inline-block'
                        }}
                      >
                        {user.vipLevel}
                      </span>
                    </span>
                  </div>
                  <div className="vip-user-info-item">
                    <i className="bi bi-calendar"></i>
                    <span style={{ display: 'block', width: '100%' }}>Joined: {formatDate(user.createdAt)}</span>
                  </div>
                </div>
                <div className="vip-user-card-footer">
                  {hasActiveKnife ? (
                    <button
                      className="knife-btn knife-btn-secondary knife-btn-sm knife-btn-block"
                      disabled
                      style={{ opacity: 0.6, cursor: 'not-allowed' }}
                      title={`User has an active knife (${currentKnifeAmount ? currentKnifeAmount.toFixed(2) + ' USDT' : 'N/A'}). Remove current knife before assigning a new one.`}
                    >
                      <i className="bi bi-lock me-1"></i>
                      Knife Active - Remove First
                    </button>
                  ) : (
                    <button
                      className="knife-btn knife-btn-primary knife-btn-sm knife-btn-block"
                      onClick={() => onAssignKnife(user.id)}
                    >
                      <i className="bi bi-dagger me-1"></i>
                      Set Knife
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Bulk Knife Assignment Modal Component
function BulkKnifeAssignmentModal({
  step,
  selectedVip,
  knifeAmount,
  reason,
  availableUsers,
  processing,
  progress,
  onClose,
  onStepChange,
  onVipSelect,
  onAmountChange,
  onReasonChange,
  onConfirm
}) {
  const vipStats = useMemo(() => {
    const stats = { VIP1: 0, VIP2: 0, VIP3: 0, VIP4: 0 };
    availableUsers.forEach(user => {
      const level = user.vipLevel || 'VIP1';
      // Fix: Check if level exists in stats object, not if it's truthy
      if (stats.hasOwnProperty(level)) {
        stats[level]++;
      }
    });
    return stats;
  }, [availableUsers]);

  const selectedUsersCount = selectedVip ? vipStats[selectedVip] : 0;
  const totalAmount = parseFloat(knifeAmount) * selectedUsersCount || 0;

  const getSuggestedAmount = (vipLevel) => {
    // Calculate based on actual user balances
    const targetUsers = availableUsers.filter(user => user.vipLevel === vipLevel);
    if (targetUsers.length === 0) {
      // Fallback to default suggestions if no users
      const defaults = {
        'VIP1': { min: 500, max: 800 },
        'VIP2': { min: 800, max: 2500 },
        'VIP3': { min: 2500, max: 8000 },
        'VIP4': { min: 8000, max: 10000 }
      };
      return defaults[vipLevel] || { min: 500, max: 1000 };
    }

    // Get all user balances
    const balances = targetUsers.map(u => u.balances?.total || u.balances?.wallet || 0);
    const maxBalance = Math.max(...balances);
    const minBalance = Math.min(...balances);
    const avgBalance = balances.reduce((sum, b) => sum + b, 0) / balances.length;

    // Calculate suggested amount: max balance + buffer (10% or minimum $1, whichever is higher)
    const buffer = Math.max(maxBalance * 0.1, 1);
    const suggestedMin = maxBalance + 0.01; // Minimum to exceed all balances
    const suggestedOptimal = maxBalance + buffer; // Optimal with buffer
    const suggestedMax = Math.max(suggestedOptimal * 1.5, maxBalance * 2); // Maximum suggestion

    return {
      min: suggestedMin,
      optimal: suggestedOptimal,
      max: suggestedMax,
      maxBalance: maxBalance,
      minBalance: minBalance,
      avgBalance: avgBalance,
      userCount: targetUsers.length
    };
  };

  return (
    <div className="bulk-modal-overlay" onClick={onClose}>
      <div className="bulk-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="bulk-modal-header">
          <h2>
            <i className="bi bi-dagger me-2"></i>
            Bulk Knife Assignment
          </h2>
          <button className="bulk-modal-close" onClick={onClose}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="bulk-modal-body">
          {step === 1 && (
            <div>
              <h3 style={{ marginBottom: '20px', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                Select VIP Level
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '20px' }}>
                Choose a VIP level to assign knives to all users in that tier
              </p>
              <div className="bulk-vip-options">
                {['VIP1', 'VIP2', 'VIP3', 'VIP4'].map(level => {
                  const userCount = vipStats[level] || 0;
                  const isSelected = selectedVip === level;
                  const getVIPColor = (lvl) => {
                    const colors = {
                      'VIP1': { primary: '#3b82f6', light: '#dbeafe', border: '#93c5fd' },
                      'VIP2': { primary: '#10b981', light: '#d1fae5', border: '#6ee7b7' },
                      'VIP3': { primary: '#a855f7', light: '#e9d5ff', border: '#c084fc' },
                      'VIP4': { primary: '#f59e0b', light: '#fef3c7', border: '#fcd34d' }
                    };
                    return colors[lvl] || colors['VIP1'];
                  };
                  const colors = getVIPColor(level);
                  
                  return (
                    <label
                      key={level}
                      className={`bulk-vip-option ${isSelected ? 'selected' : ''}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '20px',
                        border: `2px solid ${isSelected ? colors.primary : '#e5e7eb'}`,
                        borderRadius: '12px',
                        cursor: userCount > 0 ? 'pointer' : 'not-allowed',
                        marginBottom: '12px',
                        background: isSelected ? colors.light : userCount === 0 ? '#f9fafb' : 'white',
                        transition: 'all 0.2s',
                        opacity: userCount === 0 ? 0.6 : 1,
                        position: 'relative'
                      }}
                    >
                      <input
                        type="radio"
                        name="vipLevel"
                        value={level}
                        checked={isSelected}
                        onChange={(e) => {
                          if (userCount > 0) {
                            onVipSelect(e.target.value);
                          }
                        }}
                        disabled={userCount === 0}
                        style={{ marginRight: '16px', width: '20px', height: '20px', cursor: userCount > 0 ? 'pointer' : 'not-allowed' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontWeight: '700', 
                          fontSize: '1.125rem', 
                          marginBottom: '6px',
                          color: isSelected ? colors.primary : '#1f2937',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {level}
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            background: colors.primary,
                            color: 'white'
                          }}>
                            {userCount} {userCount === 1 ? 'user' : 'users'}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {userCount === 0 ? (
                            <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>No users available</span>
                          ) : (
                            <span>Ready for bulk assignment</span>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <i className="bi bi-check-circle-fill" style={{ color: colors.primary, fontSize: '1.5rem' }}></i>
                      )}
                      {userCount === 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          fontSize: '0.75rem',
                          color: '#9ca3af',
                          fontStyle: 'italic'
                        }}>
                          Empty
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
              {selectedVip && vipStats[selectedVip] > 0 && (
                <div style={{ 
                  marginTop: '20px', 
                  padding: '16px', 
                  background: 'linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%)', 
                  borderRadius: '12px',
                  border: '2px solid #6366f1',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <i className="bi bi-info-circle-fill" style={{ color: '#6366f1', fontSize: '1.25rem' }}></i>
                  <div>
                    <strong style={{ color: '#1f2937', display: 'block', marginBottom: '4px' }}>
                      Selected: {selectedVip}
                    </strong>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      {vipStats[selectedVip]} {vipStats[selectedVip] === 1 ? 'user will' : 'users will'} receive the knife assignment
                    </span>
                  </div>
                </div>
              )}
              {selectedVip && vipStats[selectedVip] === 0 && (
                <div style={{ 
                  marginTop: '20px', 
                  padding: '16px', 
                  background: '#fef2f2', 
                  borderRadius: '12px',
                  border: '2px solid #fca5a5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <i className="bi bi-exclamation-triangle-fill" style={{ color: '#dc2626', fontSize: '1.25rem' }}></i>
                  <div>
                    <strong style={{ color: '#991b1b', display: 'block', marginBottom: '4px' }}>
                      No users available
                    </strong>
                    <span style={{ color: '#7f1d1d', fontSize: '0.875rem' }}>
                      There are no users with {selectedVip} level. Please select a different VIP level.
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 style={{ marginBottom: '20px', fontSize: '1.125rem', fontWeight: '600' }}>Set Knife Amount</h3>
              <div style={{ marginBottom: '16px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                <strong>Target: {selectedVip} Users ({vipStats[selectedVip]})</strong>
              </div>
              
              {selectedVip && (() => {
                const suggestion = getSuggestedAmount(selectedVip);
                return (
                  <div style={{ 
                    marginBottom: '20px', 
                    padding: '16px', 
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
                    borderRadius: '12px',
                    border: '1px solid #fbbf24',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <i className="bi bi-lightbulb-fill" style={{ color: '#f59e0b', fontSize: '1.25rem', marginTop: '2px' }}></i>
                    <div style={{ flex: 1 }}>
                      <strong style={{ color: '#92400e', display: 'block', marginBottom: '8px', fontSize: '0.9375rem' }}>
                        Suggested Amount (Based on All User Balances)
                      </strong>
                      <div style={{ fontSize: '0.875rem', color: '#78350f', marginBottom: '8px' }}>
                        <div style={{ marginBottom: '4px' }}>
                          <strong>Balance Analysis:</strong>
                        </div>
                        <div style={{ marginLeft: '12px', lineHeight: '1.6' }}>
                          • Highest balance: <strong>{suggestion.maxBalance.toFixed(2)} USDT</strong>
                        </div>
                        <div style={{ marginLeft: '12px', lineHeight: '1.6' }}>
                          • Lowest balance: <strong>{suggestion.minBalance.toFixed(2)} USDT</strong>
                        </div>
                        <div style={{ marginLeft: '12px', lineHeight: '1.6' }}>
                          • Average balance: <strong>{suggestion.avgBalance.toFixed(2)} USDT</strong>
                        </div>
                        <div style={{ marginLeft: '12px', lineHeight: '1.6' }}>
                          • Total users: <strong>{suggestion.userCount}</strong>
                        </div>
                      </div>
                      <div style={{ 
                        padding: '12px', 
                        background: 'white', 
                        borderRadius: '8px',
                        border: '1px solid #fbbf24',
                        marginTop: '8px'
                      }}>
                        <div style={{ fontSize: '0.75rem', color: '#92400e', marginBottom: '6px', fontWeight: '600' }}>
                          RECOMMENDED AMOUNTS:
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.875rem', color: '#78350f' }}>Minimum (exceeds all):</span>
                            <strong style={{ color: '#92400e', fontSize: '0.9375rem' }}>
                              {suggestion.min.toFixed(2)} USDT
                            </strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.875rem', color: '#78350f' }}>Optimal (with buffer):</span>
                            <strong style={{ color: '#f59e0b', fontSize: '1rem' }}>
                              {suggestion.optimal.toFixed(2)} USDT
                            </strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.875rem', color: '#78350f' }}>Maximum:</span>
                            <strong style={{ color: '#92400e', fontSize: '0.9375rem' }}>
                              {suggestion.max.toFixed(2)} USDT
                            </strong>
                          </div>
                        </div>
                        <button
                          onClick={() => onAmountChange(suggestion.optimal.toFixed(2))}
                          style={{
                            marginTop: '12px',
                            width: '100%',
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(245, 158, 11, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(245, 158, 11, 0.3)';
                          }}
                        >
                          <i className="bi bi-cursor-fill me-1"></i>
                          Use Optimal Amount ({suggestion.optimal.toFixed(2)} USDT)
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="knife-form-group" style={{ marginBottom: '20px' }}>
                <label className="knife-form-label" style={{ marginBottom: '8px' }}>
                  Knife Amount (USDT) <span className="knife-required">*</span>
                  <span style={{ fontSize: '0.75rem', color: '#dc2626', marginLeft: '8px', fontWeight: '600' }}>
                    MUST exceed all users' balances
                  </span>
                </label>
                <div className="knife-input-wrapper">
                  <input
                    type="number"
                    className="knife-form-input"
                    placeholder="0.00"
                    value={knifeAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
                        onAmountChange(value);
                      }
                    }}
                    min="0"
                    step="0.01"
                    required
                    style={{ fontSize: '1rem' }}
                  />
                  <span className="knife-input-suffix">USDT</span>
                </div>
                {knifeAmount && parseFloat(knifeAmount) > 0 && selectedVip && (() => {
                  const targetUsers = availableUsers.filter(user => user.vipLevel === selectedVip);
                  const maxBalance = Math.max(...targetUsers.map(u => u.balances?.total || u.balances?.wallet || 0), 0);
                  const knifeAmountNum = parseFloat(knifeAmount);
                  const isValid = knifeAmountNum > maxBalance;
                  const invalidCount = targetUsers.filter(u => {
                    const balance = u.balances?.total || u.balances?.wallet || 0;
                    return knifeAmountNum <= balance;
                  }).length;
                  
                  return (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '8px' }}>
                        <strong>Balance Analysis:</strong>
                        <div style={{ marginTop: '4px' }}>
                          • Highest user balance: <strong>{maxBalance.toFixed(2)} USDT</strong>
                        </div>
                        <div>
                          • Total users: {targetUsers.length}
                        </div>
                      </div>
                      {isValid ? (
                        <div style={{ 
                          fontSize: '0.75rem', 
                          padding: '12px',
                          borderRadius: '8px',
                          marginTop: '8px',
                          background: '#d1fae5',
                          color: '#065f46',
                          border: '1px solid #10b981'
                        }}>
                          <i className="bi bi-check-circle-fill me-1"></i>
                          <strong>Valid:</strong> Knife amount ({knifeAmountNum.toFixed(2)} USDT) exceeds all users' balances.
                          <div style={{ marginTop: '6px' }}>
                            All {targetUsers.length} users will have negative balance created.
                          </div>
                        </div>
                      ) : (
                        <div style={{ 
                          fontSize: '0.75rem', 
                          padding: '12px',
                          borderRadius: '8px',
                          marginTop: '8px',
                          background: '#fee2e2',
                          color: '#991b1b',
                          border: '1px solid #dc2626'
                        }}>
                          <i className="bi bi-exclamation-triangle-fill me-1"></i>
                          <strong>Invalid:</strong> Knife amount ({knifeAmountNum.toFixed(2)} USDT) is not greater than all users' balances.
                          <div style={{ marginTop: '6px' }}>
                            <strong>{invalidCount} user(s)</strong> have balance ≥ {knifeAmountNum.toFixed(2)} USDT.
                          </div>
                          <div style={{ marginTop: '4px', fontWeight: '600' }}>
                            Minimum required: <strong>{(maxBalance + 0.01).toFixed(2)} USDT</strong>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div className="knife-form-group">
                <label className="knife-form-label">
                  Reason <span className="knife-optional">(Optional)</span>
                </label>
                <input
                  type="text"
                  className="knife-form-input"
                  placeholder="Enter reason for bulk assignment..."
                  value={reason}
                  onChange={(e) => onReasonChange(e.target.value)}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 style={{ marginBottom: '20px', fontSize: '1.125rem', fontWeight: '600', color: '#1f2937' }}>
                Confirm Bulk Assignment
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '20px' }}>
                Review the details below before confirming the bulk knife assignment
              </p>
              
              <div style={{ 
                padding: '24px', 
                background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)', 
                borderRadius: '16px', 
                marginBottom: '20px',
                border: '2px solid #e5e7eb'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  marginBottom: '20px',
                  paddingBottom: '16px',
                  borderBottom: '2px solid #e5e7eb'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: '700'
                  }}>
                    {selectedUsersCount}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>Target Users</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '700', color: '#1f2937' }}>
                      {selectedVip} Level
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Amount per user:</span>
                    <strong style={{ fontSize: '1.125rem', color: '#1f2937' }}>
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(parseFloat(knifeAmount) || 0)}
                    </strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Number of users:</span>
                    <strong style={{ fontSize: '1.125rem', color: '#1f2937' }}>
                      {selectedUsersCount} {selectedUsersCount === 1 ? 'user' : 'users'}
                    </strong>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '12px',
                    background: 'white',
                    borderRadius: '8px',
                    border: '2px solid #10b981',
                    marginTop: '12px'
                  }}>
                    <span style={{ color: '#059669', fontSize: '0.875rem', fontWeight: '600' }}>Total amount:</span>
                    <strong style={{ fontSize: '1.5rem', color: '#059669', fontWeight: '700' }}>
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(totalAmount)}
                    </strong>
                  </div>
                </div>

                {reason && (
                  <div style={{ 
                    padding: '12px', 
                    background: 'white', 
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    marginTop: '16px'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>
                      REASON
                    </div>
                    <div style={{ color: '#1f2937', fontSize: '0.875rem' }}>{reason}</div>
                  </div>
                )}
              </div>

              {processing && (
                <div style={{ 
                  padding: '20px', 
                  background: 'linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 100%)', 
                  borderRadius: '12px', 
                  marginBottom: '20px',
                  border: '2px solid #6366f1'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px', 
                    marginBottom: '12px' 
                  }}>
                    <div className="knife-spinner" style={{ width: '20px', height: '20px', borderColor: '#6366f1', borderTopColor: 'white' }}></div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>
                      Processing: {progress.current} / {progress.total} users
                    </div>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '12px', 
                    background: '#e5e7eb', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    marginBottom: '8px'
                  }}>
                    <div 
                      style={{ 
                        width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`, 
                        height: '100%', 
                        background: 'linear-gradient(90deg, #6366f1, #4f46e5)',
                        transition: 'width 0.3s ease',
                        borderRadius: '8px'
                      }}
                    ></div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'center' }}>
                    {progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}% complete
                  </div>
                </div>
              )}

              {!processing && (
                <div style={{ 
                  padding: '16px', 
                  background: '#fef3c7', 
                  borderRadius: '12px',
                  border: '1px solid #fbbf24',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <i className="bi bi-exclamation-triangle-fill" style={{ color: '#f59e0b', fontSize: '1.125rem', marginTop: '2px' }}></i>
                  <div>
                    <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>Important Notice</div>
                    <div style={{ fontSize: '0.875rem', color: '#78350f' }}>
                      This action will assign a knife of {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(parseFloat(knifeAmount) || 0)} to {selectedUsersCount} {selectedUsersCount === 1 ? 'user' : 'users'}. 
                      The knife will automatically apply when each user submits their next task.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bulk-modal-footer">
          {step > 1 && (
            <button
              className="knife-btn knife-btn-secondary"
              onClick={() => onStepChange(step - 1)}
              disabled={processing}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Back
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button
              className="knife-btn knife-btn-secondary"
              onClick={onClose}
              disabled={processing}
            >
              Cancel
            </button>
            {step < 3 ? (
              <button
                className="knife-btn knife-btn-primary"
                onClick={() => {
                  if (step === 1 && selectedVip) {
                    onStepChange(2);
                  } else if (step === 2 && knifeAmount) {
                    onStepChange(3);
                  }
                }}
                disabled={
                  !selectedVip || 
                  (selectedVip && vipStats[selectedVip] === 0) ||
                  (step === 2 && (!knifeAmount || parseFloat(knifeAmount) <= 0)) ||
                  (step === 2 && (() => {
                    const targetUsers = availableUsers.filter(user => user.vipLevel === selectedVip);
                    if (targetUsers.length === 0) return true;
                    const maxBalance = Math.max(...targetUsers.map(u => u.balances?.total || u.balances?.wallet || 0), 0);
                    return parseFloat(knifeAmount) <= maxBalance;
                  })())
                }
              >
                Next: {step === 1 ? 'Set Amount' : 'Confirm'}
                <i className="bi bi-arrow-right ms-1"></i>
              </button>
            ) : (
              <button
                className="knife-btn knife-btn-primary"
                onClick={onConfirm}
                disabled={processing || !knifeAmount}
              >
                {processing ? (
                  <>
                    <span className="knife-spinner"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    Confirm Assignment
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManualKnifeManagement;
